import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import TemplateCard from './components/TemplateCard';
import CategoryFilter from './components/CategoryFilter';
import FilterSidebar from './components/FilterSidebar';
import TemplatePreviewModal from './components/TemplatePreviewModal';
import StatsOverview from './components/StatsOverview';
import FeaturedTemplates from './components/FeaturedTemplates';
import { supabase } from '../../lib/supabase';
import { pagesService, auditService } from '../../lib/services';

const TemplateLibrary = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    sortBy: 'popular',
    showFree: true,
    showPro: true,
    industry: '',
    responsive: false,
    seoOptimized: false,
    darkMode: false,
    ecommerce: false,
    singlePage: false,
    multiPage: false,
    complexPage: false
  });
  const navigate = useNavigate();
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [applyError, setApplyError] = useState('');

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard-home', icon: 'Home' },
    { label: 'Template Library', path: '/template-library', icon: 'Layout' }
  ];

  useEffect(() => {
    const loadTemplates = async () => {
      setLoading(true);
      setError('');
      try {
        const { data, error: dbError } = await supabase
          ?.from('templates')
          ?.select('*')
          ?.order('downloads', { ascending: false });
        if (dbError) throw dbError;
        setTemplates(data || []);
      } catch (err) {
        setError(err?.message || 'Failed to load templates');
      } finally {
        setLoading(false);
      }
    };
    loadTemplates();
  }, []);

  const toCamelTemplate = (t) => ({
    id: t?.id,
    name: t?.name,
    description: t?.description,
    thumbnail: t?.thumbnail,
    thumbnailAlt: t?.thumbnail_alt,
    previewImage: t?.preview_image,
    previewImageAlt: t?.preview_image_alt,
    category: t?.category,
    pages: t?.pages,
    rating: t?.rating,
    downloads: t?.downloads >= 1000 ? `${(t?.downloads / 1000)?.toFixed(1)}K` : String(t?.downloads),
    isPro: t?.is_pro,
    isNew: t?.is_new,
    isFeatured: t?.is_featured,
    fullDescription: t?.full_description,
    features: t?.features || [],
    includedPages: t?.included_pages || [],
    specifications: t?.specifications || [],
    demoUrl: t?.demo_url,
    lastUpdated: t?.updated_at ? new Date(t?.updated_at)?.toLocaleDateString() : '',
  });

  const allTemplates = templates?.map(toCamelTemplate);
  const featuredTemplates = allTemplates?.filter(t => t?.isFeatured);

  const categoryMap = {
    all: allTemplates?.length,
    business: allTemplates?.filter(t => t?.category === 'business')?.length,
    ecommerce: allTemplates?.filter(t => t?.category === 'ecommerce')?.length,
    portfolio: allTemplates?.filter(t => t?.category === 'portfolio')?.length,
    blog: allTemplates?.filter(t => t?.category === 'blog')?.length,
    landing: allTemplates?.filter(t => t?.category === 'landing')?.length,
  };

  const categories = [
    { id: 'all', name: 'All Templates', icon: 'Grid', count: categoryMap?.all },
    { id: 'business', name: 'Business', icon: 'Briefcase', count: categoryMap?.business },
    { id: 'ecommerce', name: 'E-commerce', icon: 'ShoppingCart', count: categoryMap?.ecommerce },
    { id: 'portfolio', name: 'Portfolio', icon: 'Image', count: categoryMap?.portfolio },
    { id: 'blog', name: 'Blog', icon: 'FileText', count: categoryMap?.blog },
    { id: 'landing', name: 'Landing Page', icon: 'Rocket', count: categoryMap?.landing },
  ];

  const totalDownloads = allTemplates?.reduce((sum, t) => {
    const num = parseFloat(String(t?.downloads)?.replace('K', '')) * (String(t?.downloads)?.includes('K') ? 1000 : 1);
    return sum + (isNaN(num) ? 0 : num);
  }, 0);

  const stats = [
    {
      label: 'Total Templates',
      value: String(allTemplates?.length),
      icon: 'Layout',
      bgColor: 'bg-primary/10',
      iconColor: 'text-primary',
      trend: 'From database'
    },
    {
      label: 'Pro Templates',
      value: String(allTemplates?.filter(t => t?.isPro)?.length),
      icon: 'Crown',
      bgColor: 'bg-accent/10',
      iconColor: 'text-accent',
      trend: 'Premium templates'
    },
    {
      label: 'Free Templates',
      value: String(allTemplates?.filter(t => !t?.isPro)?.length),
      icon: 'Gift',
      bgColor: 'bg-success/10',
      iconColor: 'text-success',
      trend: 'Free to use'
    },
    {
      label: 'Total Downloads',
      value: totalDownloads >= 1000 ? `${(totalDownloads / 1000)?.toFixed(1)}K` : String(totalDownloads),
      icon: 'Download',
      bgColor: 'bg-secondary/10',
      iconColor: 'text-secondary',
      trend: 'All time'
    }
  ];

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      sortBy: 'popular',
      showFree: true,
      showPro: true,
      industry: '',
      responsive: false,
      seoOptimized: false,
      darkMode: false,
      ecommerce: false,
      singlePage: false,
      multiPage: false,
      complexPage: false
    });
  };

  const handlePreview = (template) => {
    setSelectedTemplate(template);
  };

  const handleUseTemplate = async (template) => {
    setApplyingTemplate(true);
    setApplyError('');
    try {
      // Increment download count
      await supabase
        ?.from('templates')
        ?.update({ downloads: (template?.rawDownloads || 0) + 1 })
        ?.eq('id', template?.id);

      // Build initial HTML content from template data
      const includedPages = template?.includedPages?.length > 0
        ? template?.includedPages?.map(p => `<li>${p}</li>`)?.join('')
        : '';
      const features = template?.features?.length > 0
        ? template?.features?.map(f => `<li>${f}</li>`)?.join('')
        : '';

      const initialContent = `<h1>${template?.name || 'New Page'}</h1>
<p>${template?.fullDescription || template?.description || ''}</p>
${features ? `<h2>Features</h2><ul>${features}</ul>` : ''}
${includedPages ? `<h2>Included Pages</h2><ul>${includedPages}</ul>` : ''}`;

      // Create a new page from the template
      const slug = `${(template?.name || 'template')?.toLowerCase()?.replace(/[^a-z0-9]+/g, '-')}-${Date.now()}`;

      // Map template category to valid page_type enum values: 'static', 'home', 'blog', 'service'
      const categoryToPageType = {
        blog: 'blog',
        home: 'home',
        service: 'service',
      };
      const pageType = categoryToPageType?.[template?.category] || 'static';

      const { data: newPage, error: createError } = await pagesService?.create({
        title: template?.name || 'New Page from Template',
        slug,
        content: initialContent,
        status: 'draft',
        pageType,
      });

      if (createError) throw createError;

      // Log the action
      await auditService?.log('CREATE', 'page', newPage?.id, null, {
        title: newPage?.title,
        source: 'template',
        templateId: template?.id,
        templateName: template?.name,
      });

      setSelectedTemplate(null);
      // Navigate to content editor with the new page
      navigate(`/content-editor?pageId=${newPage?.id}`);
    } catch (err) {
      setApplyError(err?.message || 'Failed to apply template. Please try again.');
    } finally {
      setApplyingTemplate(false);
    }
  };

  const filteredTemplates = allTemplates?.filter((template) => {
    if (activeCategory !== 'all' && template?.category?.toLowerCase() !== activeCategory) return false;
    if (searchQuery && !template?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) &&
      !template?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())) return false;
    if (!filters?.showFree && !template?.isPro) return false;
    if (!filters?.showPro && template?.isPro) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />
      <Header isCollapsed={sidebarCollapsed} userName="Admin" userRole="Administrator" />
      <main className={`main-content with-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="p-4 md:p-6 lg:p-8 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                Template Library
              </h1>
              <Breadcrumbs items={breadcrumbItems} />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
              <Icon name="AlertCircle" size={16} />
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="Loader2" size={32} className="text-primary animate-spin mr-3" />
              <span className="text-muted-foreground text-lg">Loading templates...</span>
            </div>
          ) : (
            <>
              <StatsOverview stats={stats} />

              {featuredTemplates?.length > 0 && (
                <FeaturedTemplates
                  templates={featuredTemplates}
                  onPreview={handlePreview}
                  onUse={handleUseTemplate}
                />
              )}

              <div className="flex flex-col lg:flex-row items-start gap-4 md:gap-6">
                <div className="w-full lg:w-64 flex-shrink-0">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="lg:hidden w-full mb-4 flex items-center justify-between px-4 py-3 bg-card border border-border rounded-lg text-foreground hover:bg-muted transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <Icon name="SlidersHorizontal" size={20} />
                      <span className="font-medium">Filters</span>
                    </span>
                    <Icon name={showFilters ? 'ChevronUp' : 'ChevronDown'} size={20} />
                  </button>
                  <div className={`${showFilters ? 'block' : 'hidden'} lg:block`}>
                    <FilterSidebar
                      filters={filters}
                      onFilterChange={handleFilterChange}
                      onClearFilters={handleClearFilters}
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0 space-y-6">
                  <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                    <div className="flex flex-col md:flex-row gap-4 mb-6">
                      <div className="flex-1">
                        <Input
                          type="search"
                          placeholder="Search templates by name, category, or features..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e?.target?.value)}
                        />
                      </div>
                    </div>
                    <CategoryFilter
                      categories={categories}
                      activeCategory={activeCategory}
                      onCategoryChange={setActiveCategory}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-sm md:text-base text-muted-foreground">
                      Showing <span className="font-medium text-foreground">{filteredTemplates?.length}</span> templates
                    </p>
                  </div>

                  {filteredTemplates?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                      {filteredTemplates?.map((template) => (
                        <TemplateCard
                          key={template?.id}
                          template={template}
                          onPreview={handlePreview}
                          onUse={handleUseTemplate}
                          applying={applyingTemplate}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="bg-card rounded-lg border border-border p-8 md:p-12 text-center">
                      <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                        <Icon name="Search" size={32} className="text-muted-foreground" />
                      </div>
                      <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">No templates found</h3>
                      <p className="text-sm md:text-base text-muted-foreground mb-6">
                        Try adjusting your filters or search query
                      </p>
                      <Button
                        variant="outline"
                        iconName="RotateCcw"
                        iconPosition="left"
                        onClick={() => {
                          setSearchQuery('');
                          setActiveCategory('all');
                          handleClearFilters();
                        }}
                      >
                        Reset Filters
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </main>
      {selectedTemplate && (
        <TemplatePreviewModal
          template={selectedTemplate}
          onClose={() => { setSelectedTemplate(null); setApplyError(''); }}
          onUse={handleUseTemplate}
          applying={applyingTemplate}
          applyError={applyError}
        />
      )}
    </div>
  );
};

export default TemplateLibrary;